<template>
  <div v-if="nap">
    <div class="mb-4">
      <v-btn
        color="grey"
        variant="text"
        to="/nap/list"
      >{{ $t('nap.backToList') }}</v-btn>
    </div>
    <div>
      <v-progress-linear
        v-if="isUpdating"
        indeterminate
        color="primary"
        class="my-8"
      ></v-progress-linear>
    </div>

    <div></div>

    <div class="d-flex align-center mb-8">
      <div>
        <div class="d-flex align-center">
          <h1>{{ $t('nap.napCheck') }}</h1>
          <div class="ml-4">
            <v-chip v-if="napStatus === 'inProgress'" dark color="#EE6190">{{ $t('nap.status.inProgress') }}</v-chip>
            <v-chip v-if="napStatus === 'finished'" dark color="#029ec0">{{ $t('nap.status.finished') }}</v-chip>
          </div>
        </div>
        <h2>{{ subTitle }}</h2>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex">
        <div class="mr-2">
          <v-btn
            v-if="napStatus === 'inProgress'"
            size="large"
            variant="flat"
            color="primary"
            :disabled="isUpdating"
            @click="updateNapStatus('finished')"
          >{{ $t('nap.endNap') }}</v-btn>
          <v-btn
            v-if="napStatus === 'finished'"
            size="large"
            variant="flat"
            color="primary"
            :disabled="isUpdating"
            @click="updateNapStatus('inProgress')"
          >{{ $t('nap.resumeNap') }}</v-btn>
          <v-btn
            v-if="napStatus === 'applied'"
            size="large"
            variant="flat"
            color="primary"
            :disabled="isUpdating"
            @click="updateNapStatus('finished')"
          >{{ $t('nap.withdrawApplication') }}</v-btn>
        </div>
        <v-menu location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              color="grey"
              variant="text"
              v-bind="props"
              rounded="pill"
              size="large"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="() => isDeleteModalOpen = true">
              <v-list-item-title class="text-red">{{ $t('common.delete') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <v-divider class="my-8"></v-divider>

    <div class="my-4">
      <v-row align="center">
        <v-text-field
          variant="outlined"
          hide-details
          :label="$t('nap.comment')"
          v-model="comment"
          :disabled="isUpdating"
        ></v-text-field>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          size="large"
          :disabled="isUpdating"
          @click="saveNap"
        >{{ $t('common.save') }}</v-btn>
      </v-row>
    </div>
    
    <nap-detail-air-condition-container></nap-detail-air-condition-container>

    <div class="my-8">
      <v-row>
        <v-col cols="3">
          <h4 class="">{{ $t('nap.recorder') }}</h4>
          <v-select
            variant="outlined"
            density="compact"
            hide-details
            :items="staffOptions"
            item-value="userId"
            item-title="name"
            v-model="recorder"
          >
          </v-select>
        </v-col>
        <v-col cols="9">
          <div class="d-flex mb-1">
            <v-spacer></v-spacer>
            <v-chip size="small" color="primary">{{ $t('attendance.selectTime') }}</v-chip>
            <v-spacer></v-spacer>
          </div>
          <v-slide-group
            v-model="selectedHour"
            show-arrows
            class="mb-4"
          >
            <v-slide-group-item
              v-for="h in hours"
              :key="h"
              :value="h"
            >
              <v-btn
                size="small"
                color="primary"
                style="border-radius: 0px;"
                :variant="selectedHour === h ? 'flat' : 'outlined'"
                @click="selectedHour = h"
              >
                {{ h }}
              </v-btn>
            </v-slide-group-item>
          </v-slide-group>
        </v-col>
      </v-row>
    </div>

    <div class="nap-grid-container">
      <div class="nap-grid">
        <div class="nap-grid-col wide fixed-left">

          <div class="nap-gird-info nap-grid-header">
            <div class="col-childName header-cell">
              {{ $t('attendance.childName') }}
            </div>
            <div class="col-className header-cell">
              {{ $t('common.class') }}
            </div>
            <div class="col-status header-cell">
              {{ $t('common.status') }}
            </div>
          </div>

          <div 
            class="nap-gird-info"
            v-for="data in napData"
            :key="data.child.childId"
          >
            <div class="col-childName cell">
              {{ data.child.lastName }} {{ data.child.firstName }}
            </div>
            <div class="col-className cell">
              {{ getCurrentClassName(data.child) }}
            </div>
            <div :class="['col-status', 'cell', `${getLatestStatus(data.overviews)}`]">
              {{ getLatestStatusLabel(data.overviews) }}
            </div>
          </div>
        </div>

        <div
          class="nap-grid-col"
          v-for="(hour, i) in dataCols"
          :key="i"
        >
          <div class="nap-grid-header col">
            {{ hour }}
          </div>

          <div 
            class="nap-grid-item cell"
            v-for="data in napData"
            :key="data.child.childId"
          >
            <template v-if="getNapDataStatus(data, hour)">
              <div
                v-if="getNapDataStatus(data, hour).isIcon"
                class="col-data-cell-event"
                :style="`color: ${getNapDataStatus(data, hour).color}; background-color: ${getNapDataStatus(data, hour).bg};`"
                @click="cellClickEdit(data, hour)"
              >
                <v-icon color="white"> {{ getNapDataStatus(data, hour).iconName }} </v-icon>
                <div v-html="getNapDataStatus(data, hour).label"></div>
              </div>
              <div
                v-else
                class="col-data-cell-event"
                :style="`color: ${getNapDataStatus(data, hour).color}; background-color: ${getNapDataStatus(data, hour).bg};`"
                v-html="getNapDataStatus(data, hour).label"
                @click="cellClickEdit(data, hour)"
              ></div>
            </template>
            <div
              v-else
              class="col-data-cell-event"
              @click="cellClickCreate(data.child.childId, hour)"
            >
              <v-icon color="grey">mdi-plus</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <nap-detail-data-create-container></nap-detail-data-create-container>
    <nap-detail-data-edit-container></nap-detail-data-edit-container>

    <v-dialog
      v-model="isDeleteModalOpen"
      max-width="540"
    >
      <v-card>
        <div class="pa-4">
          <div class="d-flex my-8">
            <v-spacer></v-spacer>
            {{ $t('nap.confirmDeleteNap') }}
            <v-spacer></v-spacer>
          </div>

          <div>
            <v-progress-linear
              v-if="isDeleting"
              indeterminate
              color="red"
              class="my-8"
            ></v-progress-linear>
          </div>

          <div class="d-flex my-8">
            <v-spacer></v-spacer>
            <v-btn
              variant="flat"
              color="grey"
              class="mr-2"
              size="large"
              dark
              @click="() => isDeleteModalOpen = false"
            >{{ $t('common.cancel') }}</v-btn>
            <v-btn
              variant="flat"
              color="red"
              size="large"
              @click="deleteNap"
              dark
            >{{ $t('common.delete') }}</v-btn>
            <v-spacer></v-spacer>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
// スクリプト部分は元のロジックをそのまま維持しています
import { defineComponent } from 'vue';

import { napDetailModule, NapDetailData, poseOptions } from '@/store/dataModules/napDetailModule';
import { NurseryUserResponse } from 'chaild-api/src/component/nursery/user/types';
import { NurseryChildResponse, NurseryNapDetailResponse, NurseryNapIndividualOverview } from 'chaild-api/lib';
import dayjs from 'dayjs';

import NapDetailDataCreateContainer from '@/containers/NapDetailDataCreateContainer.vue';
import NapDetailDataEditContainer from '@/containers/NapDetailDataEditContainer.vue';
import NapDetailAirConditionContainer from '@/containers/NapDetailAirConditionContainer.vue';

import ApiNap, { NapMonitoringData } from '@/api/ApiNap';
import LocalDataService from '@/service/LocalDataService';
import { nurseryUserModule } from '@/store/dataModules/nurseryUserModule';
import CommonUtil from '@/utils/CommonUtil';
import { formatAgeFilterLabel } from '@/constants/attendance';

interface DataType {
  selectedHour: number;
  isUpdating: boolean;
  isDeleteModalOpen: boolean;
  isDeleting: boolean;
}

interface StaffOption {
  userId: number;
  name: string;
}

export default defineComponent({
  components: {
    NapDetailDataCreateContainer,
    NapDetailDataEditContainer,
    NapDetailAirConditionContainer,
  },

  data: (): DataType => {
    return {
      selectedHour: 0,
      isUpdating: false,
      isDeleteModalOpen: false,
      isDeleting: false,
    };
  },

  computed: {
    nap(): NurseryNapDetailResponse | null {
      return napDetailModule.nap;
    },
    napStatus(): string | null {
      if (this.nap) {
        return this.nap.status;
      }
      return null;
    },
    napData() {
      return napDetailModule.napData;
    },
    staffs() {
      return napDetailModule.staffs;
    },
    children(): NurseryChildResponse[] {
      if (this.nap) {
        return this.nap.children;
      }
      return [];
    },
    staffOptions() {
      return napDetailModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`
        }
      });
    },
    subTitle(): string {
      if (this.nap) {
        const dateLabel = dayjs(this.nap.date).format('DD/MM/YYYY (ddd)');
        const classLabel = this.nap.class.all ?
          this.$t('nap.allAges') :
          this.nap.class.classes.map((c) => c.className).join(', ');
        const ageLabel = this.nap.age.all ?
          this.$t('nap.allAges') :
          this.nap.age.ages.map((a) => formatAgeFilterLabel(a)).join(', ');
        return `${dateLabel} ${classLabel} / ${ageLabel}`;
      }
      return '';
    },
    hours(): number[] {
      return [...Array(24)].map((e, i) => {
        return i
      });
    },
    hoursAm(): number[] {
      return [...Array(12)].map((e, i) => {
        return i
      });
    },
    hoursPm(): number[] {
      return [...Array(12)].map((e, i) => {
        return i + 12
      });
    },
    dataCols(): string[] {
      if (this.nap) {
        const start = dayjs(this.nap.createdAt).hour(this.selectedHour).startOf('hour');
        return [...Array(12)].map((e, i) => {
          const time = dayjs(start).add(5 * i, 'minutes').format('HH:mm');
          return time;
        });
      }
      return [];
    },
    comment: {
      get(): string | null {
        if (this.nap) {
          return this.nap.comment;
        }
        return null;
      },
      set(value: string) {
        if (this.nap) {
          const newNap: NurseryNapDetailResponse = {...this.nap};
          newNap.comment = value;
          napDetailModule.setNap(newNap);
        }
      }
    },
    recorder: {
      get(): number | null {
        if (napDetailModule.nap) {
          return napDetailModule.nap.recorderId;
        }
        return null;
      },
      set(value: number) {
        if (this.nap) {
          const newNap: NurseryNapDetailResponse = {...this.nap};
          newNap.recorderId = value;
          napDetailModule.setNap(newNap);
          napDetailModule.updateNap();
        }
      }
    },
  },

  methods: {
    getCurrentClassName(child: NurseryChildResponse) {
      if (this.nap) {
        const date = dayjs(this.nap.date);
        const regulator = date.month() < 4 ? -1 : 0; 
        const thisYear = parseInt(CommonUtil.getFiscalYear(this.nap.date))
        const find = child.nurseryClasses.find((c) => c.year === thisYear);
        if (find) {
          return find.className;
        }
      }
      return '';
    },
    getLatestStatus(overview: NurseryNapIndividualOverview[]) {
      if (overview.length === 0) {
        return 'none';
      }
      const sorted = [...overview].sort((o1, o2) => {
        const time1 = dayjs(o1.time);
        const time2 = dayjs(o2.time);
        return time2.diff(time1);
      });
      return sorted[0].status;
    },
    getLatestStatusLabel(overview: NurseryNapIndividualOverview[]) {
      const status = this.getLatestStatus(overview);
      if (status === 'none') {
        return this.$t('nap.statusLabels.none');
      }
      if (status === 'getUp') {
        return this.$t('nap.statusLabels.awake');
      }
      return this.$t('nap.statusLabels.sleeping');
    },
    cellClickCreate(childId: number, time: string) {
      if (this.nap) {
        const napData: NapDetailData = {
          childId: childId,
          time: time,
          staffId: this.nap?.recorderId,
          posturalChanged: null,
          status: null,
          additionalNote: [],
          textComment: null,
        };
        napDetailModule.setCreatingNapDetailData(napData);
      }
    },
    cellClickEdit(data: NapMonitoringData, hour: string) {
      const napData = this.getNapData(data, hour);
      if (this.nap && napData) {
        const editData: NapDetailData = {
          childId: data.child.childId,
          time: hour,
          staffId: napData.staff.userId,
          posturalChanged: napData.posturalChanged || null,
          status: napData.status,
          additionalNote: napData.additionalNote || [],
          textComment: '',
        };

        napDetailModule.setEditingNapDetailData(editData);
      }
    },
    getNapData(data: NapMonitoringData, hour: string) {
      const key = hour.replace(':', '');
      const find = data.overviews.find((ov) => ov.time === key);
      if (find) {
        return find;
      }
      return null;
    },
    getNapDataStatus(data: NapMonitoringData, hour: string) {
      const thisData = this.getNapData(data, hour);
      if (thisData) {
        const returnValue: {
          label?: string;
          bg?: string;
          color?: string;
          isIcon?: boolean;
          iconName?: string;
        } = {
          label: '',
        };

        if (thisData.status) {
          switch (thisData.status) {
            case 'sleepIn':
              returnValue.label = `<div class="data-cell-status">${this.$t('nap.statusLabels.sleepIn')}</div>`;
              returnValue.bg = '#029ec0';
              returnValue.isIcon = false;
              returnValue.color = 'white';
              break;
            case 'faceLeft':
              returnValue.iconName = 'mdi-arrow-left';
              returnValue.isIcon = true;
              returnValue.bg = '#02C07C';
              returnValue.color = 'white';
              break;
            case 'faceRight':
              returnValue.iconName = 'mdi-arrow-right';
              returnValue.bg = '#02C07C';
              returnValue.isIcon = true;
              returnValue.color = 'white';
              break;
            case 'faceDown':
              returnValue.iconName = 'mdi-arrow-down';
              returnValue.isIcon = true;
              returnValue.bg = '#02C07C';
              returnValue.color = 'white';
              break;
            case 'faceUp':
              returnValue.iconName = 'mdi-arrow-up';
              returnValue.isIcon = true;
              returnValue.bg = '#02C07C';
              returnValue.color = 'white';
              break;
            case 'getUp':
              returnValue.label = `<div class="data-cell-status">${this.$t('nap.statusLabels.getUp')}</div>`;
              returnValue.bg = '#EE6190';
              returnValue.color = 'white';
              break;
            default:
              break;
          }
        }

        if (thisData.posturalChanged) {
          switch (thisData.posturalChanged) {
            case 'leftToUp':
              returnValue.label = returnValue.label + `<div class="data-cell-pose">${this.$t('nap.postureChanges.leftToUp')}</div>`
              break;
            case 'rightToUp':
              returnValue.label = returnValue.label + `<div class="data-cell-pose">${this.$t('nap.postureChanges.rightToUp')}</div>`
              break;
            case 'downToUp':
              returnValue.label = returnValue.label + `<div class="data-cell-pose">${this.$t('nap.postureChanges.downToUp')}</div>`
              break;
            default:
              break;
          }
        }

        returnValue.label = returnValue.label + `<div class="data-cell-staff">${thisData.staff.lastName}</div>`

        return returnValue;
      }
    },
    async saveNap() {
      if (this.nap) {
        this.isUpdating = true;
        await napDetailModule.updateNap();
        this.isUpdating = false;
      }
    },
    async updateNapStatus(status: string) {
      if (this.nap) {
        this.isUpdating = true;
        await napDetailModule.updateNapStatus(status);
        this.isUpdating = false;
      }
    },
    async deleteNap() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.nap) {
        this.isDeleting = true;
        const response = await ApiNap.deleteNap({
          napId: this.nap.napId,
          nurseryId,
        });
        if (response) {
          this.$router.push('/nap/list')
        } else {
          this.isDeleting = false;
          alert(this.$t('nap.deleteFailed'));
        }
      }
    },
  },

  async mounted() {
    const napIdParam = this.$route.params.napId;
    const napIdStr = typeof napIdParam === "string" ? napIdParam : napIdParam?.[0];
    if (napIdStr) {
      try {
        const parsed = parseInt(napIdStr, 10);
        await Promise.all([
          napDetailModule.setNapId(parsed),
          napDetailModule.getNap(),
          napDetailModule.listNapData(),
        ]);

        if (this.nap) {
          const date = dayjs();
          this.selectedHour = date.hour();
        }

      } catch {
        return;
      }
    }
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      if (this.nap) {
        napDetailModule.listStaffs({
          date: this.nap.date,
          diaryId: this.nap.napId,
          diaryType: 'nap',
        });
        nurseryUserModule.listStaffs({
          date: this.nap.date,
          diaryId: this.nap.napId,
          diaryType: 'nap'
        });
      }
      else {
        nurseryUserModule.listStaffs({});
      }
    }
  }
});
</script>

<style lang="scss">
$fixed-cols-width: 300px;
$record-header-height: 40px;
$record-row-height: 80px;
$record-cell-height: 79px;

.nap-grid-container {
  display: grid;
  overflow: auto;
  height: calc(100vh - 100px);
  width: 100%;
}

.nap-grid {
  display: flex;
  flex-wrap: nowrap;
}

.nap-grid-col {
  width: 120px;
  &.wide {
    width: 220px;
  }

  &.fixed-left {
    position: sticky;
    /* ⭕️ 修正9: start -> left */
    left: 0;
    background: white;
    z-index: 3;

    border-right: solid 1px #c4c4c4;
  }
}

.nap-gird-info {
  display: flex;
  align-items: stretch;
}

.nap-grid-header {
  position: sticky;
  background: white;
  z-index: 2;
  top: 0;

  border-bottom: solid 1px #c4c4c4;

  &.col {
    height: $record-header-height;
    display: flex;
    justify-content: center;
    align-items: center;

    font-size: 14px;
  }
}

.nap-grid-item {
  border: solid 1px #f0f0f0;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
    opacity: .8;
  }
}

.nap-record-table {
  display: flex;
}
.record-fixed-cols {
  width: $fixed-cols-width;
  border-right: solid 1px #c4c4c4;
}
.record-scrollable-cols {
  flex: 1;
  overflow-x: scroll;
  height: auto;
}

.record-row {
  display: flex;
  align-items: center;
  height: $record-row-height;

  border-bottom: solid 1px #c4c4c4;
  padding-bottom: 1px;

  &.fixed {
    width: $fixed-cols-width;
  }
  &.header {
    height: $record-header-height;
    border-bottom: solid 1px #c4c4c4;
    font-weight: bold;
  }
  &.data {
    width: 1440px;
  }
}

.header {
  font-size: 12px;
}

.header-cell {
  height: $record-header-height;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cell {
  height: $record-cell-height;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.col-childName {
  width: 120px;
  font-size: 14px;
}
.col-className {
  width: 100px;
  font-size: 14px;
}
.col-status {
  width: 100px;
  font-size: 14px;
  &.none {
    background-color: #eaeaea;
  }
  &.getUp {
    background-color: #EE6190;
    color: white;
  }
  &.sleepIn {
    background-color: #029ec0;
    color: white;
  }
}
.col-data {
  width: 120px;
  text-align: center;
}

.scroll-wrapper {
  width: 1440px;
}

.col-data-cell {
  width: 120px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
}

.col-data-cell-event {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:hover {
    opacity: .8;
  }
}

.data-cell-status {
  font-weight: bold;
}

.data-cell-pose {
  font-size: 12px;
}

.data-cell-staff {
  font-size: 12px;
  margin-top: 8px;
}
</style>